 
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChangelogEntry, ChangelogPriorityEnum, ChangelogResponse } from '@uptime-bff/api-contract';

interface ChangelogState {
  displayWhatIsNew: boolean;
  changelogEntries: { [key: string]: ChangelogResponse };
  whatIsNewEntries: ChangelogEntry[];
  unreadWhatIsNewCount: number;
}

export const defaultState: ChangelogState = {
  displayWhatIsNew: false,
  changelogEntries: {},
  whatIsNewEntries: [],
  unreadWhatIsNewCount: 0,
};

const changelogEntriesToMap = (list: ChangelogResponse[]): { [key: string]: ChangelogResponse } => {
  const payloadChangelogEntriesMap: { [key: string]: ChangelogResponse } = {};
  for (const changelogEntry of list) {
    payloadChangelogEntriesMap[changelogEntry.changelog.changelogId] = { ...changelogEntry };
  }
  return payloadChangelogEntriesMap;
};

const slice = createSlice({
  name: 'changelog',
  initialState: defaultState,
  reducers: {
    setDisplayWhatIsNew: (state, action: PayloadAction<boolean>) => ({
      ...state,
      displayWhatIsNew: action.payload,
    }),

    setChangelogEntry: (state, action: PayloadAction<ChangelogResponse[]>) => {
      const newEntries = changelogEntriesToMap(action.payload);
      state.changelogEntries = newEntries;
    },

    setUnreadWhatIsNewCount: (state, action: PayloadAction<number>) => ({
      ...state,
      unreadWhatIsNewCount: action.payload,
    }),

    setWhatIsNewEntries: (state, action: PayloadAction<ChangelogEntry[]>) => {
      const incomingIds = action.payload.map((e) => e.changelogId).sort((a, b) => a.localeCompare(b)).join(',');
      const storedIds = (state.whatIsNewEntries ?? []).map((e) => e.changelogId).sort((a, b) => a.localeCompare(b)).join(',');

      if (incomingIds === storedIds) {
        state.displayWhatIsNew = false;
      } else {
        state.whatIsNewEntries = action.payload;
        state.displayWhatIsNew = action.payload.length > 0;
      }
    },

    setChangelogEntryAsRead: (state, action: PayloadAction<string>) => {
      const changelogEntry = state.changelogEntries[action.payload];
      if (changelogEntry && !changelogEntry.isRead) {
        state.changelogEntries[action.payload].isRead = true;
        if (changelogEntry.changelog.priority === ChangelogPriorityEnum.MEDIUM
          || changelogEntry.changelog.priority === ChangelogPriorityEnum.HIGH) {
          state.unreadWhatIsNewCount -= 1;
        }
      }
    },
  },
});

export const {
  setDisplayWhatIsNew,
  setChangelogEntry,
  setWhatIsNewEntries,
  setUnreadWhatIsNewCount,
  setChangelogEntryAsRead
} = slice.actions;

export default slice.reducer;
